import { Spinner } from 'components/spinner/Spinner';
import {
    RouteCorporation,
    RouteLocation,
    RouteSystem,
    useRouteEntities,
} from 'contexts/RouteEntityContext';
import { ReactNode } from 'react';
import { ProtectedContent, ProtectedContentProps } from 'routing/ProtectedContent';

export type EntityContentProps<TOptions> = Readonly<{
    render: (renderOptions: TOptions) => ReactNode;
}>;

/**
 * Renders the supplied content after the URL corporation is available.
 * The corporation will be passed to the render function.
 */
export function CorporationContent({ render }: EntityContentProps<RouteCorporation>) {
    const { isLoading, corporation } = useRouteEntities();

    if (isLoading) {
        return <Spinner />;
    }

    if (!corporation) {
        throw new Error('Missing route entity.');
    }

    return <>{render({ corporation })}</>;
}

/**
 * Renders the supplied content after the URL location is available.
 * The location will be passed to the render function.
 */
export function LocationContent({ render }: EntityContentProps<RouteLocation>) {
    const { isLoading, corporation, location } = useRouteEntities();

    if (isLoading) {
        return <Spinner />;
    }

    if (!corporation || !location) {
        throw new Error('Missing route entity.');
    }

    return <>{render({ corporation, location })}</>;
}

/**
 * Renders the supplied content after the URL system is available.
 * The system will be passed to the render function.
 */
export function SystemContent({ render }: EntityContentProps<RouteSystem>) {
    const { isLoading, corporation, location, system, session } = useRouteEntities();

    if (isLoading) {
        return <Spinner />;
    }

    if (!corporation || !location || !system) {
        throw new Error('Missing route entity.');
    }

    return <>{render({ corporation, location, system, session })}</>;
}

export type EntityRouteProps<TOptions> = Omit<ProtectedContentProps, 'children'> &
    EntityContentProps<TOptions>;

/**
 * Creates a protected route the renders the supplied content after the URL corporation
 * is available. The corporation will be passed to the render function.
 */
export function ProtectedCorporationContent({
    render,
    ...protectedRouteProps
}: EntityRouteProps<RouteCorporation>) {
    return (
        <ProtectedContent {...protectedRouteProps}>
            <CorporationContent render={render} />
        </ProtectedContent>
    );
}

/**
 * Creates a protected route the renders the supplied content after the URL location
 * is available. The location will be passed to the render function.
 */
export function ProtectedLocationContent({
    render,
    ...protectedRouteProps
}: EntityRouteProps<RouteLocation>) {
    return (
        <ProtectedContent {...protectedRouteProps}>
            <LocationContent render={render} />
        </ProtectedContent>
    );
}

/**
 * Creates a protected route the renders the supplied content after the URL system
 * is available. The system will be passed to the render function.
 */
export function ProtectedSystemContent({
    render,
    ...protectedRouteProps
}: EntityRouteProps<RouteSystem>) {
    return (
        <ProtectedContent {...protectedRouteProps}>
            <SystemContent render={render} />
        </ProtectedContent>
    );
}
